import * as yup from 'yup';
import { omit } from 'lodash';
import sumBy from 'lodash/fp/sumBy';
import {
  LegalShareholder,
  PhysicalPerson,
} from '../../../../global/entities/Person';
import {
  validateSwedishOrganizationNumber,
  validateSwedishSsn,
} from '../../../../utils/validation';
import { validatePerson } from '../../../../utils/validation/person';
import { numberSharesValidation } from '../../../../utils/validation/number-shares';
import { FieldProps } from '../../../../utils/types';
import { hasFieldsWithValues } from '../../../../utils';
import { OwnerSharesMetaFields } from './owner-shares-types';

export const calculateShareCapital = (numberShares: number) =>
  numberShares * 100;

const physicalPersonValidationSchema = yup.object().shape({
  ...omit(validatePerson, 'phoneNumber'),
  socialSecurityNumber: validateSwedishSsn(true),
  numberShares: numberSharesValidation,
});

const legalShareholderValidationSchema = yup.object().shape({
  firstName: validatePerson.firstName,
  lastName: validatePerson.lastName,
  socialSecurityNumber: validateSwedishSsn(true),
  organizationName: yup.string().trim().required('Organisationens namn krävs'),
  organizationPostalCode: validatePerson.postalCode,
  organizationAddress: validatePerson.address,
  organizationCity: validatePerson.city,
  organizationNumber: validateSwedishOrganizationNumber.required(
    'Organisationsnummer krävs',
  ),
  numberShares: numberSharesValidation.required('Antal aktier krävs'),
});

export const ownerSharesValidationSchema = yup.object().shape({
  physicalPeople: yup
    .array()
    .of(physicalPersonValidationSchema)
    .compact((value) => !hasFieldsWithValues(value)),

  legalShareholders: yup
    .array()
    .of(legalShareholderValidationSchema)
    .compact((value) => !hasFieldsWithValues(value)),

  remainingShares: yup
    .mixed()
    .test({
      exclusive: false,
      params: {},
      message: 'Du har kvar aktier!',
      test() {
        const { physicalPeople, legalShareholders } = this.parent;
        const totalShares = getTotalShares(physicalPeople, legalShareholders);
        const remainingShares = totalShares - this.parent.numberShares;

        return !(remainingShares < 0);
      },
    })
    .test({
      exclusive: false,
      params: {},
      message: 'Du kan inte tilldela fler aktier än du har!',
      test() {
        const { physicalPeople, legalShareholders } = this.parent;
        const totalShares = getTotalShares(physicalPeople, legalShareholders);
        const remainingShares = totalShares - this.parent.numberShares;

        return !(remainingShares > 0);
      },
    }),
});

const getTotalShares = (
  physicalPeople: PhysicalPerson[],
  legalShareholders: LegalShareholder[],
) => {
  const sumShares = sumBy((x: any) => Number(x?.numberShares));

  return sumShares(physicalPeople) + sumShares(legalShareholders);
};

export const isShareCapitalValid = (
  shareCapital: number,
): shareCapital is 25_000 | 50_000 => {
  if (shareCapital !== 25_000 && shareCapital !== 50_000) {
    throw new Error('Share capital must be 25,000 or 50,000.');
  }

  return true;
};

export const isNumberSharesValid = (
  numberShares: number,
): numberShares is 250 | 500 => {
  if (numberShares !== 250 && numberShares !== 500) {
    throw new Error(`Number shares must be 250 or 500, but is ${numberShares}`);
  }

  return true;
};

export const OwnerSharesMetaFieldNames: FieldProps<OwnerSharesMetaFields> = {
  minShareholders: 'minShareholders',
  remainingShares: 'remainingShares',
};
